import {t} from "../i18n.js"

/**
 * WebAuthn hook — bridges the browser WebAuthn API to Phoenix LiveView events.
 *
 * Usage in HEEx:
 *   <div id="webauthn-hook" phx-hook="WebAuthn"></div>
 *
 * LiveView pushes:
 *   "webauthn:register_challenge"    → { options: PublicKeyCredentialCreationOptions }
 *   "webauthn:authenticate_challenge"→ { options: PublicKeyCredentialRequestOptions }
 *
 * Hook pushes back:
 *   "webauthn:register_response"     → credential response JSON
 *   "webauthn:authenticate_response" → assertion response JSON
 *   "webauthn:error"                 → { message }
 */
export default {
  mounted() {
    // Registration: server sends creation options → browser creates credential
    this.handleEvent("webauthn:register_challenge", async ({ options }) => {
      try {
        const credential = await navigator.credentials.create({
          publicKey: decodeCreationOptions(options)
        })
        this.pushEvent("webauthn:register_response", encodeCredential(credential))
      } catch (err) {
        this.pushEvent("webauthn:error", { message: webauthnErrorMessage(err) })
      }
    })

    // Authentication: server sends request options → browser gets assertion
    this.handleEvent("webauthn:authenticate_challenge", async ({ options }) => {
      try {
        const assertion = await navigator.credentials.get({
          publicKey: decodeRequestOptions(options)
        })
        this.pushEvent("webauthn:authenticate_response", encodeAssertion(assertion))
      } catch (err) {
        this.pushEvent("webauthn:error", { message: webauthnErrorMessage(err) })
      }
    })
  }
}

// ---- Encoding helpers -------------------------------------------------------

/**
 * Decodes Base64URL fields in PublicKeyCredentialCreationOptions so the browser
 * WebAuthn API receives the expected ArrayBuffer types.
 */
function decodeCreationOptions(opts) {
  return {
    ...opts,
    challenge: base64urlToBuffer(opts.challenge),
    user: {
      ...opts.user,
      id: base64urlToBuffer(opts.user.id)
    },
    excludeCredentials: (opts.excludeCredentials || []).map(c => ({
      ...c,
      id: base64urlToBuffer(c.id)
    }))
  }
}

/**
 * Decodes Base64URL fields in PublicKeyCredentialRequestOptions.
 */
function decodeRequestOptions(opts) {
  return {
    ...opts,
    challenge: base64urlToBuffer(opts.challenge),
    allowCredentials: (opts.allowCredentials || []).map(c => ({
      ...c,
      id: base64urlToBuffer(c.id)
    }))
  }
}

/**
 * Encodes a PublicKeyCredential (registration) to a plain JSON-serialisable object.
 */
function encodeCredential(credential) {
  const response = credential.response
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    attestationObject: bufferToBase64url(response.attestationObject),
    clientDataJSON: bufferToBase64url(response.clientDataJSON)
  }
}

/**
 * Encodes a PublicKeyCredential (assertion / authentication) to JSON.
 */
function encodeAssertion(assertion) {
  const response = assertion.response
  return {
    id: assertion.id,
    rawId: bufferToBase64url(assertion.rawId),
    type: assertion.type,
    authenticatorData: bufferToBase64url(response.authenticatorData),
    clientDataJSON: bufferToBase64url(response.clientDataJSON),
    signature: bufferToBase64url(response.signature),
    userHandle: response.userHandle ? bufferToBase64url(response.userHandle) : null
  }
}

// ---- Base64URL ↔ ArrayBuffer -----------------------------------------------

function base64urlToBuffer(base64url) {
  // Add padding if needed
  const padded = base64url.replace(/-/g, "+").replace(/_/g, "/")
  const padLen = (4 - (padded.length % 4)) % 4
  const base64 = padded + "=".repeat(padLen)
  const binary = atob(base64)
  const buffer = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) buffer[i] = binary.charCodeAt(i)
  return buffer.buffer
}

function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ""
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "")
}

// Browser-provided DOMException messages follow the browser language, which
// can differ from the application language. Present known failures consistently.
export function webauthnErrorMessage(error) {
  switch (error?.name) {
    case "NotAllowedError":
    case "AbortError":
      return t("Passkey verification was cancelled or timed out. Try again.")
    case "InvalidStateError":
      return t("This passkey is already registered. Try another passkey.")
    case "NotSupportedError":
      return t("Passkeys are not supported by this browser or device.")
    case "SecurityError":
      return t("Passkeys require a secure connection. Check the page address.")
    case "ConstraintError":
      return t("This device cannot create the requested passkey. Try another device.")
    default:
      return t("Could not verify the passkey. Please try again.")
  }
}
